/**
 * The brand paint hint: which account last signed in here, and the override pair its colour
 * painted, remembered in `localStorage` so the next load's FIRST frame already wears it.
 *
 * Ported from the mobile app's `brandColorCache.ts` (per-account entries, a paint hint only, the
 * network read wins the moment it lands, every storage call best-effort). Two differences, both
 * forced by the browser:
 *
 * · **The pair is stored PRECOMPUTED**, both themes, rather than as the wire choice. It is read by
 *   `public/theme-init.js`, a classic script that runs before any module and so can do no colour
 *   maths. It picks the half its theme class names and writes it on `<html>`, nothing more.
 * · **A pointer names the account**, because that script runs before any session is known. It is
 *   set while the signed-in shell is on screen and removed when the shell goes, which is what keeps
 *   the sign-in screen in the default Flame after a sign-out and a reload.
 *
 * ── A persistence contract, mirrored in `public/theme-init.js` ───────────────────────────────
 * The two keys, the version and the validation below are duplicated in that script on purpose, and
 * `brandPaintHint.test.ts` writes through this module and then RUNS the script, so the two cannot
 * drift apart silently. Anything unreadable (not JSON, another version, a value that is not a
 * string, a name that is not a custom property) is ignored as a whole: an older or newer build's
 * record must never crash this one, and a half-applied colour is worse than none.
 *
 * ── A default colour is REMEMBERED too ───────────────────────────────────────────────────────
 * A trainer on Flame writes a `default` marker rather than no entry. The shell holds its first frame
 * only for an account this browser has never seen, and Flame is most trainers: with no marker, every
 * cold load of a Flame trainer would wait for the read. The boot script paints nothing for it.
 *
 * ── ONE version covers the shape and the values ──────────────────────────────────────────────
 * `v` is `BRAND_PAINT_HINT_VERSION`. It moves when the record's shape changes AND when the
 * derivation in `brandTokens.ts` changes what it returns, so an older record is ignored by both
 * readers instead of pre-painting the previous build's colours for a frame.
 */

import type { ThemeOverrides } from '~/shared/design-system/theme';
import type { ForgettingPreferenceStore } from '~/shared/lib/preferenceStore';

/** The account whose hint the pre-paint may use. Present only while its shell is on screen. */
export const BRAND_PAINT_POINTER_KEY = 'movebuddy.brandPaint.active';

const HINT_KEY_PREFIX = 'movebuddy.brandPaint:';
/** The record's shape and the derivation behind its values, in one number. See the header. */
export const BRAND_PAINT_HINT_VERSION = 3;
/** Custom property names only — the script refuses anything else, and so does this module. */
const CUSTOM_PROPERTY = /^--[a-z0-9-]+$/;

/** What this browser remembers of an account: its brand pair, or that it paints the default. */
export type RememberedBrandPaint =
  | { readonly kind: 'default' }
  | { readonly kind: 'brand'; readonly overrides: ThemeOverrides };

export function brandPaintHintKey(accountId: string): string {
  return `${HINT_KEY_PREFIX}${accountId}`;
}

function isOverrideSet(value: unknown): value is Readonly<Record<string, string>> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.entries(value).every(
    ([name, css]) => CUSTOM_PROPERTY.test(name) && typeof css === 'string',
  );
}

function parseHint(raw: string | null): RememberedBrandPaint | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const record = parsed as { v?: unknown; default?: unknown; light?: unknown; dark?: unknown };
    if (record.v !== BRAND_PAINT_HINT_VERSION) return null;
    if (record.default === true) return { kind: 'default' };
    if (!isOverrideSet(record.light) || !isOverrideSet(record.dark)) return null;
    return { kind: 'brand', overrides: { light: record.light, dark: record.dark } };
  } catch {
    return null;
  }
}

/** Every call below is best-effort: a store that throws means "nothing remembered". */
function attempt<T>(work: () => T, fallback: T): T {
  try {
    return work();
  } catch {
    return fallback;
  }
}

/** What this browser remembers of the account, or `null` when it has never seen it. */
export function readBrandPaintHint(
  store: ForgettingPreferenceStore | null,
  accountId: string,
): RememberedBrandPaint | null {
  if (store === null) return null;
  return attempt(() => parseHint(store.getItem(brandPaintHintKey(accountId))), null);
}

/** Remember what this account paints now. `null` (the default colour) writes the default marker. */
export function rememberBrandPaint(
  store: ForgettingPreferenceStore | null,
  accountId: string,
  overrides: ThemeOverrides | null,
): void {
  if (store === null) return;
  const record =
    overrides === null
      ? { v: BRAND_PAINT_HINT_VERSION, default: true }
      : { v: BRAND_PAINT_HINT_VERSION, ...overrides };
  const key = brandPaintHintKey(accountId);
  const serialized = JSON.stringify(record);
  attempt(() => {
    // Every answer lands here, most of them unchanged; a write that stores the same bytes is skipped.
    if (store.getItem(key) !== serialized) store.setItem(key, serialized);
  }, undefined);
}

/** Forget an account entirely — its entry, and the pointer if it names it. For an erased account. */
export function forgetBrandPaint(store: ForgettingPreferenceStore | null, accountId: string): void {
  if (store === null) return;
  attempt(() => {
    store.removeItem(brandPaintHintKey(accountId));
    if (store.getItem(BRAND_PAINT_POINTER_KEY) === accountId) {
      store.removeItem(BRAND_PAINT_POINTER_KEY);
    }
  }, undefined);
}

/** The account the pointer names, or `null`. */
export function readBrandPaintAccount(store: ForgettingPreferenceStore | null): string | null {
  if (store === null) return null;
  const accountId = attempt(() => store.getItem(BRAND_PAINT_POINTER_KEY), null);
  return accountId === '' ? null : accountId;
}

/** Point the pre-paint at this account, or at nobody. */
export function pointBrandPaintAt(
  store: ForgettingPreferenceStore | null,
  accountId: string | null,
): void {
  if (store === null) return;
  attempt(() => {
    if (accountId === null) store.removeItem(BRAND_PAINT_POINTER_KEY);
    else store.setItem(BRAND_PAINT_POINTER_KEY, accountId);
  }, undefined);
}
